import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { htmlToExcerpt } from "../../lib/utils";
import PageHero from "../../components/public/PageHero";
import { ChevronLeft, ChevronRight, MapPin, ArrowUpRight } from "lucide-react";

// Sub-categorías del cecit.es original — se aplican SOLO cuando la vista es "actividades"
// (i.e. cuando el prop `showFilters` es true). "Networking" no aparece porque las demás
// categorías son en sí mismas oportunidades de networking.
const ACTIVITY_CATEGORIES = [
  { key: "all", es: "Todas", fr: "Toutes" },
  { key: "encuentros", es: "Encuentros empresariales", fr: "Rencontres d'entreprises" },
  { key: "networking", es: "Networking", fr: "Networking" },
  { key: "seminarios", es: "Seminarios", fr: "Séminaires" },
  { key: "salones_ferias", es: "Salones, ferias y foros", fr: "Salons, foires et forums" },
  { key: "visitas_reuniones", es: "Visitas y reuniones", fr: "Visites et réunions" },
  { key: "actos_institucionales", es: "Actos institucionales", fr: "Actes institutionnels" },
  { key: "webinars", es: "Webinars", fr: "Webinaires" },
];

const TITLES = {
  ferias_es: { es: "Ferias en España", fr: "Salons en Espagne" },
  ferias_ma: { es: "Ferias en Marruecos", fr: "Salons au Maroc" },
  camara: { es: "Eventos de la Cámara", fr: "Événements de la Chambre" },
  socios: { es: "Eventos de Socios", fr: "Événements des Membres" },
  actividad: { es: "Actividades", fr: "Activités" },
};

const PER_PAGE = 9;

export default function EventList({ category, upcoming, title, subtitle, showFilters }) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = category || params.tipo?.replace("-", "_");
  const { lang, t, pick } = useLang();
  const [items, setItems] = useState([]);
  // Initial sub-category comes from ?cat=… so deep links from the header dropdown
  // land on the right pre-filter (Encuentros, Webinars, etc.).
  const [subcat, setSubcat] = useState(() => searchParams.get("cat") || "all");
  const [page, setPage] = useState(1);

  // Keep local state in sync when the URL query param changes (menu clicks).
  useEffect(() => {
    if (!showFilters) return;
    const urlCat = searchParams.get("cat") || "all";
    if (urlCat !== subcat) setSubcat(urlCat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, showFilters]);

  const onPickSubcat = (next) => {
    setSubcat(next);
    // Reflect the filter in the URL so it can be bookmarked / shared
    const q = new URLSearchParams(searchParams);
    if (next === "all") q.delete("cat");
    else q.set("cat", next);
    setSearchParams(q, { replace: true });
  };

  useEffect(() => {
    const q = new URLSearchParams();
    if (showFilters) {
      if (subcat !== "all") {
        q.set("category", subcat);
      } else {
        q.set("family", "actividades");
      }
    } else if (cat) {
      q.set("category", cat);
    }
    if (upcoming != null) q.set("upcoming", upcoming);
    api.get(`/events?${q.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    setPage(1);
  }, [cat, upcoming, showFilters, subcat]);

  const computedTitle = title || (showFilters ? (lang === "es" ? "Actividades" : "Activités") : (TITLES[cat]?.[lang] || t("nav.eventos")));

  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const paged = useMemo(
    () => items.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [items, page]
  );

  return (
    <div data-testid={`events-page-${cat || "actividades"}`}>
      <PageHero title={computedTitle} subtitle={subtitle} />

      {showFilters && (
        <section className="border-b border-gray-100 bg-white sticky top-16 z-30" data-testid="activity-filters">
          <div className="max-w-7xl mx-auto px-6 py-4 flex gap-2 flex-wrap items-center">
            <span className="text-xs uppercase tracking-widest text-gray-500 mr-2">
              {lang === "es" ? "Categoría" : "Catégorie"}
            </span>
            {ACTIVITY_CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => onPickSubcat(c.key)}
                data-testid={`activity-filter-${c.key}`}
                className={`text-xs px-3.5 py-1.5 border transition-colors ${
                  subcat === c.key
                    ? "bg-[#0F2040] text-white border-[#0F2040]"
                    : "border-gray-200 text-gray-700 hover:border-[#0F2040]"
                }`}
              >
                {c[lang]}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-16">
        {paged.length === 0 ? (
          <p className="text-gray-500 text-center py-20" data-testid="events-empty">{t("common.noContent")}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paged.map((e) => (
              <article
                key={e.id}
                className="bg-white border border-gray-100 hover:shadow-xl transition-shadow group flex flex-col"
                data-testid={`event-card-${e.slug}`}
              >
                {e.cover_image && (
                  <div className="aspect-video overflow-hidden bg-gray-100">
                    <img
                      src={e.cover_image}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  {e.starts_at && (
                    <p className="text-xs uppercase tracking-widest text-[#C8102E] mb-3">
                      {new Date(e.starts_at).toLocaleDateString(lang === "es" ? "es-ES" : "fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  )}
                  <h3 className="font-serif text-2xl text-[#0F2040] mb-3 leading-tight">{pick(e, "title")}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3 flex-1">
                    {htmlToExcerpt(pick(e, "description"), 260)}
                  </p>
                  {e.location && (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-3">
                      <MapPin className="w-3 h-3" /> {e.location}
                    </p>
                  )}
                  <Link
                    to={`/actividades/${e.slug}`}
                    className="text-xs uppercase tracking-widest text-[#0F2040] inline-flex items-center gap-1.5 hover:text-[#C8102E] transition-colors mt-1"
                  >
                    {lang === "es" ? "Leer más" : "Lire plus"} <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-14" data-testid="events-pagination">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 border border-gray-200 hover:border-[#0F2040] disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                data-testid={`events-page-${i + 1}`}
                className={`w-9 h-9 text-sm border ${
                  page === i + 1 ? "bg-[#0F2040] text-white border-[#0F2040]" : "border-gray-200 hover:border-[#0F2040]"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 border border-gray-200 hover:border-[#0F2040] disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
